/**
 * Cita Events - Shared Redirect Security Module
 * ==============================================
 * FE-005: Open Redirect Prevention (CWE-601)
 *
 * Provides secure URL redirect functionality across all auth flows
 * (login, activation, password reset, checkout).
 *
 * Promoted from checkout-core/security.js for shared use.
 *
 * @module http/redirect
 */

/**
 * Allowed external domains for payment redirects.
 * Only these domains are permitted for window.location.href assignments
 * when the URL comes from API responses.
 *
 * @constant {string[]}
 */
export const ALLOWED_REDIRECT_DOMAINS = [
  'checkout.stripe.com',
  'billing.stripe.com',
  'connect.stripe.com',
  'js.stripe.com',
  'verify.stripe.com',
];

/**
 * Check if a URL is allowed for redirect.
 *
 * @param {string} url - The URL to validate
 * @returns {boolean} True if the URL is safe to redirect to
 */
export function isAllowedRedirectUrl(url) {
  if (!url) return false;

  // Allow relative URLs (internal navigation)
  if (url.startsWith('/') && !url.startsWith('//')) {
    return true;
  }

  try {
    const parsed = new URL(url, window.location.origin);

    // Block dangerous protocols
    if (parsed.protocol === 'javascript:' || parsed.protocol === 'data:') {
      return false;
    }

    // Allow same origin
    if (parsed.origin === window.location.origin) {
      return true;
    }

    // Check against allowed external domains
    return ALLOWED_REDIRECT_DOMAINS.some(domain =>
      parsed.hostname === domain
    );
  } catch {
    return false;
  }
}

/**
 * Safely redirect to a URL with validation.
 *
 * Validates that the URL is either:
 * 1. A relative URL (starts with /)
 * 2. Same origin as current page
 * 3. An allowed external domain (Stripe)
 *
 * If validation fails, redirects to fallback URL instead.
 *
 * @param {string} url - The URL to redirect to
 * @param {string} [fallback='/'] - Fallback URL if validation fails
 * @returns {void}
 */
export function safeRedirect(url, fallback = '/') {
  if (!url) {
    console.warn('[SECURITY] Blocked empty redirect URL');
    window.location.href = fallback;
    return;
  }

  // Allow relative URLs (internal navigation)
  if (url.startsWith('/') && !url.startsWith('//')) {
    window.location.href = url;
    return;
  }

  try {
    const parsed = new URL(url, window.location.origin);

    // Block javascript: and data: protocols
    if (parsed.protocol === 'javascript:' || parsed.protocol === 'data:') {
      console.warn('[SECURITY] Blocked dangerous protocol:', parsed.protocol);
      window.location.href = fallback;
      return;
    }

    // Allow same origin
    if (parsed.origin === window.location.origin) {
      window.location.href = url;
      return;
    }

    // Check against allowed external domains
    const isAllowed = ALLOWED_REDIRECT_DOMAINS.some(domain =>
      parsed.hostname === domain
    );

    if (isAllowed) {
      window.location.href = url;
    } else {
      console.warn('[SECURITY] Blocked redirect to unauthorized domain:', parsed.hostname);
      window.location.href = fallback;
    }
  } catch (e) {
    console.warn('[SECURITY] Invalid redirect URL:', url, e);
    window.location.href = fallback;
  }
}

/**
 * Validate a redirect URL and return a safe version (without performing the redirect).
 * Useful in contexts where you need the URL but handle navigation yourself.
 *
 * @param {string} url - The URL to validate
 * @param {string} [fallback='/login'] - Fallback if URL is unsafe
 * @returns {string} The validated URL or fallback
 */
export function safeRedirectUrl(url, fallback = '/login') {
  if (!url || typeof url !== 'string') return fallback;
  if (url.startsWith('/') && !url.startsWith('//')) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.protocol === 'javascript:' || parsed.protocol === 'data:') return fallback;
    if (parsed.origin === window.location.origin) return parsed.pathname + parsed.search + parsed.hash;
  } catch { /* malformed */ }
  return fallback;
}

export default {
  ALLOWED_REDIRECT_DOMAINS,
  isAllowedRedirectUrl,
  safeRedirect,
  safeRedirectUrl,
};
